import React, { useState, useEffect, useCallback } from 'react';
import { getDocumentProperties, setDocumentProperty, loadDocumentFromBase64 } from '../services/wordHelper';
import { getAuthHeaders, API_URL } from '../services/auth';
import { confirm } from './ConfirmDialog';
import './TemplateInfo.css';

interface TemplateData {
  Id: number;
  Name: string;
  Description: string;
  DocumentType: string;
  Status: string;
  CurrentVersion: number;
  CategoryId: number | null;
  WorkflowId: number | null;
  CreatedAt: string;
  UpdatedAt: string;
  hasDraft: boolean;
  draftVersion: number | null;
  publishedVersion: number | null;
  totalVersions: number;
}

interface VersionEntry {
  Id: number;
  VersionNumber: number;
  VersionStatus: string;
  Changelog: string;
  FileSize: number;
  UploadedAt: string;
  CreatedBy: string;
  PublishedAt: string | null;
}

interface Category {
  Id: number;
  Name: string;
}

interface DocType {
  Id: number;
  Name: string;
}

interface Workflow {
  Id: number;
  Name: string;
}

async function apiFetch(path: string, options?: RequestInit) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...(options?.headers || {}) },
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
}

export function TemplateInfo() {
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Version history
  const [showAllVersions, setShowAllVersions] = useState(false);

  // Auto-load template from document properties
  useEffect(() => {
    (async () => {
      try {
        const props = await getDocumentProperties();
        const id = props['dp_templateId'];
        if (id) {
          setTemplateId(Number(id));
        } else {
          setLoading(false);
          setError('No template linked to this document. Save the template first.');
        }
      } catch {
        setLoading(false);
        setError('Could not read document properties.');
      }
    })();
  }, []);

  // Load template data when ID is set
  const loadTemplate = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    setError(null);
    try {
      const tmplRes = await apiFetch(`/templates/${templateId}`);

      // Fetch optional data — don't fail if these error
      let dtRes = { data: [] };
      let cats: Category[] = [];
      let wfs: Workflow[] = [];
      try { dtRes = await apiFetch('/document-types'); } catch { /* optional */ }
      try {
        const catRes = await apiFetch('/categories');
        cats = (catRes.data || []).map((c: any) => ({ Id: c.Id || c.id, Name: c.Name || c.name }));
      } catch { /* optional */ }
      try {
        const wfRes = await apiFetch('/workflows');
        wfs = (wfRes.data || []).map((w: any) => ({ Id: w.Id || w.id, Name: w.Name || w.name }));
      } catch { /* optional */ }

      const t = tmplRes.data;
      setTemplate(t);
      setVersions(t.versions || []);
      setCategories(cats);
      setDocTypes((dtRes.data || []).map((d: any) => ({ Id: d.Id, Name: d.Name })));
      setWorkflows(wfs);
    } catch (err: any) {
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  // Lock/Unlock
  const handleLock = async (lock: boolean) => {
    if (!templateId) return;
    try {
      await apiFetch(`/templates/${templateId}/${lock ? 'checkout' : 'checkin'}`, { method: 'POST' });
      setMessage({ type: 'success', text: lock ? 'Template locked' : 'Template unlocked' });
      await loadTemplate();
    } catch {
      setMessage({ type: 'error', text: `Failed to ${lock ? 'lock' : 'unlock'} template` });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Revert to version
  const handleRevert = async (versionNumber: number) => {
    if (!templateId) return;
    const ok = await confirm({
      title: `Load version ${versionNumber} into the document?`,
      description: 'Current changes will be replaced.',
      confirmLabel: 'Load',
      danger: true,
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/doc-service/templates/${templateId}/versions/${versionNumber}/file`, {
        headers: { ...getAuthHeaders() },
      });
      if (!response.ok) throw new Error('Version file not found');
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
        reader.readAsDataURL(blob);
      });
      await loadDocumentFromBase64(base64);
      await setDocumentProperty('dp_versionNumber', String(versionNumber));
      setMessage({ type: 'success', text: `Loaded version ${versionNumber}` });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to load version' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const formatSize = (bytes: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'active': return '#166534';
      case 'draft': return '#b45309';
      case 'pendingapproval': return '#1d4ed8';
      case 'published': return '#166534';
      case 'archived': return '#6b7280';
      default: return '#374151';
    }
  };

  if (loading) return <div className="dp-ti__loading">Loading template options...</div>;

  if (error && !template) {
    return (
      <div className="dp-ti">
        <div className="dp-ti__error">{error}</div>
      </div>
    );
  }

  if (!template) return null;

  const displayedVersions = showAllVersions ? versions : versions.slice(0, 5);
  const workflowName = workflows.find(w => w.Id === template.WorkflowId)?.Name;
  const categoryName = categories.find(c => c.Id === template.CategoryId)?.Name;

  return (
    <div className="dp-ti">
      {/* Message banner */}
      {message && (
        <div className={`dp-ti__msg dp-ti__msg--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Section 1: Template Details */}
      <div className="dp-ti__section">
        <div className="dp-ti__section-header">Template Details</div>

        {/* Status badges row */}
        <div className="dp-ti__badges">
          <span className="dp-ti__badge" style={{ background: statusColor(template.Status) + '18', color: statusColor(template.Status), border: `1px solid ${statusColor(template.Status)}40` }}>
            {template.Status}
          </span>
          {template.publishedVersion && (
            <span className="dp-ti__badge dp-ti__badge--green">Published v{template.publishedVersion}</span>
          )}
          {template.hasDraft && template.draftVersion && (
            <span className="dp-ti__badge dp-ti__badge--amber">Draft v{template.draftVersion}</span>
          )}
        </div>

        {/* Name */}
        <div className="dp-ti__field">
          <label>Name</label>
          <span className="dp-ti__value">{template.Name}</span>
        </div>

        {/* Document Type */}
        <div className="dp-ti__field">
          <label>Document Type</label>
          <span className="dp-ti__value">{template.DocumentType || 'General'}</span>
        </div>

        {/* Category */}
        <div className="dp-ti__field">
          <label>Category</label>
          <span className="dp-ti__value">{categoryName || 'No category'}</span>
        </div>

        {/* Description */}
        {template.Description && (
          <div className="dp-ti__field">
            <label>Description</label>
            <span className="dp-ti__value">{template.Description}</span>
          </div>
        )}

        {/* Meta info */}
        <div className="dp-ti__meta">
          <span>ID: {template.Id}</span>
          <span>Created: {formatDate(template.CreatedAt)}</span>
          <span>Updated: {formatDate(template.UpdatedAt)}</span>
        </div>
      </div>

      {/* Section 2: Version History */}
      <div className="dp-ti__section">
        <div className="dp-ti__section-header">
          Version History
          <span className="dp-ti__section-count">{template.totalVersions} versions</span>
        </div>

        <div className="dp-ti__versions">
          {displayedVersions.map(v => (
            <div key={v.Id} className="dp-ti__version">
              <div className="dp-ti__version-top">
                <span className="dp-ti__version-num">v{v.VersionNumber}</span>
                <span
                  className="dp-ti__version-status"
                  style={{ color: v.VersionStatus === 'Published' ? '#166534' : '#b45309' }}
                >
                  {v.VersionStatus || 'Draft'}
                </span>
                <span className="dp-ti__version-size">{formatSize(v.FileSize)}</span>
              </div>
              <div className="dp-ti__version-bottom">
                <span className="dp-ti__version-date">
                  {formatDate(v.PublishedAt || v.UploadedAt)}
                </span>
                {v.Changelog && v.Changelog !== `Version ${v.VersionNumber}` && (
                  <span className="dp-ti__version-notes">{v.Changelog}</span>
                )}
              </div>
              <button
                className="dp-ti__btn dp-ti__btn--small"
                onClick={() => handleRevert(v.VersionNumber)}
                title="Load this version into the document"
              >
                Load
              </button>
            </div>
          ))}
        </div>

        {versions.length > 5 && (
          <button
            className="dp-ti__btn dp-ti__btn--link"
            onClick={() => setShowAllVersions(!showAllVersions)}
          >
            {showAllVersions ? 'Show less' : `Show all ${versions.length} versions`}
          </button>
        )}
      </div>

      {/* Section 3: Settings */}
      <div className="dp-ti__section">
        <div className="dp-ti__section-header">Settings</div>

        {/* Workflow */}
        <div className="dp-ti__setting">
          <span className="dp-ti__setting-label">Approval Workflow</span>
          <span className="dp-ti__setting-value">{workflowName || 'None assigned'}</span>
        </div>

        {/* Lock */}
        <div className="dp-ti__setting">
          <span className="dp-ti__setting-label">Lock Status</span>
          <span className="dp-ti__setting-value">Not locked</span>
        </div>
      </div>
    </div>
  );
}
